import classNames from 'classnames';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../../store/types';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import { TemplateClassNames } from '../../../../types/template-layouts';
import {
	LanguagesAbbr,
	RegistrationStepType,
	SessionTypesEnum,
	Speaker,
	SpeakerAttribute as SpeakerAttributeType
} from '../../../../types/working-model';
import { renderSocialLinks } from '../../../admin/create/homepage/editor/speakers/speaker';
import { EIcon } from '../../../general-ui/icon/icon';
import ModalComponent from '../../../general-ui/modal/modal';
import { Tag } from '../../../general-ui/tag/tag';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { eventPath, stylingOverrideReturns } from '../../utils';
import './speaker-details-modal.scss';

interface Props {
	speaker: Speaker;
	template: TemplateClassNames;
	language: LanguagesAbbr | 'base';
	stylingOverrides: stylingOverrideReturns;
	onClose: () => void;
	relatedSessions: number[];
	params: ParamsProps,
	onRelatedSessionClick: (path: string, state?: unknown) => void;
}
export const SpeakerDetailsModal = ({
	speaker,
	language,
	stylingOverrides,
	template,
	onClose,
	relatedSessions,
	params,
	onRelatedSessionClick
}: Props): JSX.Element => {

	const { moduleTitleOverrides, primaryButtonOverrides } = stylingOverrides;

	const socialLinks = speaker.link_social ? renderSocialLinks(speaker.social_links || [], primaryButtonOverrides) : null;

	const speakerAttributes = speaker?.attributes ? (
		speaker?.attributes.map((attribute, idx) => (
			<SpeakerAttribute key={idx} attribute={attribute} template={template} language={language} />
		))) : (
		null
	);

	return (
		<ModalComponent
			className={classNames(
				'ev-modal-wrapper live-event-container person-details-modal',
				template
			)}
			title=""
			open={true}
			onRequestClose={onClose}
			closeable
			cancellable
		>
			<div className="person-details-modal-content">
				<div className="person-details-image">
					<img src={speaker.image} alt={speaker.first_name} width="164" height="164" />
				</div>

				<div className="person-details-content">
					<TypographyItem
						className={classNames('person-details-name stable', moduleTitleOverrides, {
							'evt-heading-3': template === TemplateClassNames.Apollo,
							'evt-heading-4': template !== TemplateClassNames.Apollo,
						})}
						role="heading"
						aria-level={3}
					>
						{speaker.first_name} {speaker.last_name}
						<Tag text="Speaker" classNameTag="tag-container-speaker" />
					</TypographyItem>
					<TypographyItem className={classNames('evt-body-text-2 person-details-position')} tagName="h4">
						{speaker?.job_title?.[language] || speaker?.job_title?.base}
					</TypographyItem>
					{socialLinks}
					<TypographyItem className={classNames('evt-body-text-2 person-details-description')} tagName="p">
						{speaker.description[language] || speaker.description.base}
					</TypographyItem>

					<ul className="personal-data-list">
						{speakerAttributes}
					</ul>
				</div>

				<SessionList template={template} relatedSessions={relatedSessions} params={params} onRelatedSessionClick={onRelatedSessionClick} />
			</div>
		</ModalComponent>
	);
};

interface SessionListProps {
	template: TemplateClassNames;
	relatedSessions: number[];
	params: ParamsProps;
	onRelatedSessionClick: (path: string, state?: unknown) => void;
}
const SessionList = ({ template, relatedSessions, params, onRelatedSessionClick }: SessionListProps): JSX.Element => {
	return (
		<div className="person-details-sessions">
			<div className="person-details-sessions-header">
				<TypographyItem
					className={classNames('session-label stable', {
						'evt-caption-1': template === TemplateClassNames.Apollo || template === TemplateClassNames.O2,
						'evt-caption-2': template === TemplateClassNames['Studio 54'],
					})}
					tagName="h4"
				>
					Sessions ({relatedSessions.length})
				</TypographyItem>
			</div>
			<ul>
				{
					relatedSessions
						? relatedSessions.map((id, idx) => (
							<SessionItem
								key={id}
								template={template}
								sessionId={id}
								params={params}
								onRelatedSessionClick={onRelatedSessionClick}
								idx={idx}
							/>
						))
						: null
				}
			</ul>
		</div>
	);
};

interface SessionItemProps {
	template: TemplateClassNames;
	sessionId: number;
	params: ParamsProps;
	onRelatedSessionClick: (path: string, state?: unknown) => void
	idx: number;
}

const SessionItem = ({ template, sessionId, params, onRelatedSessionClick, idx }: SessionItemProps): JSX.Element => {
	const {
		LiveEventReducer: {
			eventBundle,
			registrationId,
			validRegistration,
		}
	} = useSelector((state: EventsState) => state);

	const { language, eventName, uuid } = params;

	const session = useMemo(() => {
		return eventBundle?.sessions?.find(session => session.session === sessionId);
	}, [eventBundle, sessionId]);

	const sessionLink = useMemo(() => {
		if (!session) return "";
		const ticketingStep = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.ticketing && step.isOn);
		let sessionHasTicket = false;

		if (ticketingStep) {
			const sessionTickets = ticketingStep.ticketing?.filter(ticket => ticket.session_uuids.indexOf(session.uuid) >= 0) || [];

			if (sessionTickets?.length > 0) {
				sessionHasTicket = true;
			}
		}

		if (eventBundle?.registration_on && !validRegistration) {
			let baseUrl = `/${eventPath(eventName, uuid)}/${language}/registration`;

			if (sessionHasTicket) {
				baseUrl = `${baseUrl}/${session.uuid}`;
			}

			return baseUrl;
		} else {
			return `/${eventPath(eventName, uuid)}/${language}/session/${session.uuid}`;
		}
	}, [eventBundle, validRegistration, uuid, language, session, eventName]);

	const handleSessionClick = () => {
		if (eventBundle?.registration_on && !registrationId) {
			onRelatedSessionClick(`/${eventPath(eventName, uuid)}/${language}/registration`);
		} else {
			if (!sessionLink) return null;
			onRelatedSessionClick(sessionLink);
		}
	};

	return (
		<li className="session-item">
			<div className="session-item-image">
				{session?.image ?
					<img src={session.image} width="88" height="88" aria-hidden="true" />
					:
					<img src={getDefaultAgendaImage(template, idx)} width="88" height="88" aria-hidden="true" />
				}
			</div>
			<div className="session-item-content">
				<TypographyItem
					className={classNames('session-item-date stable', {
						'evt-caption-3': template === TemplateClassNames.Apollo,
						'evt-body-text-2': template !== TemplateClassNames.Apollo,
					})}
					tagName="time"
				>
					{session && session.session_type === SessionTypesEnum.onDemand && new Date(session.timestamp!).toLocaleDateString()}
				</TypographyItem>
				<TypographyItem
					className={classNames('session-item-title text-ellipsis evt-heading-5 stable')} tagName="h4">
					{session?.title[language] || session?.title.base}
				</TypographyItem>
				<button className="session-item-link no-style no-padding" onClick={handleSessionClick}>Session details</button>
			</div>
		</li>
	);
};

interface ISpeakerAttribute {
	template: TemplateClassNames;
	attribute: SpeakerAttributeType;
	language: LanguagesAbbr | 'base';
}

const SpeakerAttribute: React.FC<ISpeakerAttribute> = ({ template, attribute, language }) => {
	const { icon, attribute: attributeTitle, value } = attribute;
	return (
		<li>
			<div className="personal-data-list-icon">
				<EIcon name={icon} />
			</div>
			<div className="personal-data-list-content">
				<TypographyItem
					className={classNames('personal-data-list-label', {
						'evt-caption-3': template === TemplateClassNames.Apollo,
						'evt-body-text-2': template !== TemplateClassNames.Apollo,
					})}
				>
					{attributeTitle?.[language] ?? attributeTitle.base}
				</TypographyItem>
				<TypographyItem className={classNames('evt-body-text-2 personal-data-list-value')}>
					{value?.[language] ?? value.base}
				</TypographyItem>
			</div>
		</li>
	);
};
